import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";

import { PageListContainer } from "../../../components/Page";
import { useConfirm } from "../../../components/feedback";
import { DRAWER_FORM_GRID_SPACING } from "../../../lib/ui/dialogFormLayout";
import {
  buildMailSettingsSavePayload,
  mapFormFromResponse,
  normalizeRecipientDomains,
  SMTP_PORT_OPTIONS,
} from "../../../lib/mail-settings/form";
import {
  pageButtonOutlinedPrimaryProps,
  pageButtonPrimaryProps,
} from "../../../lib/ui/pageButtons";
import {
  EMPTY_MAIL_SETTINGS,
  getMailSettingsApiErrorMessage,
  mailSettingsService,
  type MailProvider,
  type MailSettingsSavePayload,
} from "../../../services/mailSettingsService";

type ProviderOption = { value: MailProvider; label: string };
type PortOption = { value: number; label: string };

function MailSettingsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [testTo, setTestTo] = useState("");
  const [form, setForm] = useState<MailSettingsSavePayload>(
    mapFormFromResponse(EMPTY_MAIL_SETTINGS),
  );
  const [passwordSet, setPasswordSet] = useState(false);
  const [clientSecretSet, setClientSecretSet] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState("");

  const isGraph = form.provider === "OFFICE365_GRAPH";

  const providerOptions = useMemo<ProviderOption[]>(
    () => [
      { value: "CUSTOM_SMTP", label: t("mailSettings.providerSmtp") },
      { value: "OFFICE365_GRAPH", label: t("mailSettings.providerO365") },
    ],
    [t],
  );

  const selectedProvider =
    providerOptions.find((option) => option.value === form.provider) ??
    providerOptions[0];

  const selectedPort: PortOption = SMTP_PORT_OPTIONS.find(
    (option) => option.value === form.port,
  ) ?? {
    value: form.port,
    label: String(form.port),
  };

  const portOptions = useMemo(() => {
    if (SMTP_PORT_OPTIONS.some((option) => option.value === form.port)) {
      return [...SMTP_PORT_OPTIONS];
    }
    return [
      ...SMTP_PORT_OPTIONS,
      { value: form.port, label: String(form.port) },
    ];
  }, [form.port]);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mailSettingsService.get();
      setForm(mapFormFromResponse(data));
      setDomains(data.allowedRecipientDomains || []);
      setPasswordSet(Boolean(data.passwordSet));
      setClientSecretSet(Boolean(data.clientSecretSet));
      setPassword("");
      setClientSecret("");
    } catch (loadError) {
      setError(
        getMailSettingsApiErrorMessage(loadError, t("mailSettings.loadFailed")),
      );
    } finally {
      setLoading(false);
    }
  };

  const applySaved = (
    saved: Awaited<ReturnType<typeof mailSettingsService.save>>,
  ) => {
    setPasswordSet(Boolean(saved.passwordSet));
    setClientSecretSet(Boolean(saved.clientSecretSet));
    setPassword("");
    setClientSecret("");
    setForm(mapFormFromResponse(saved));
    setDomains(saved.allowedRecipientDomains || []);
  };

  const addDomain = () => {
    const next = normalizeRecipientDomains([...domains, domainInput]);
    setDomains(next);
    setDomainInput("");
  };

  const removeDomain = async (domain: string) => {
    try {
      await confirm({
        title: t("mailSettings.removeDomainTitle"),
        description: t("mailSettings.removeDomainConfirm", { domain }),
      });
      setDomains((prev) => prev.filter((item) => item !== domain));
    } catch {
      // cancelled
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await mailSettingsService.save(
        buildMailSettingsSavePayload(form, domains, { password, clientSecret }),
      );
      applySaved(saved);
      setSuccess(t("mailSettings.saveSuccess"));
    } catch (saveError) {
      setError(
        getMailSettingsApiErrorMessage(saveError, t("mailSettings.saveFailed")),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo.trim()) {
      setError(t("mailSettings.testToRequired"));
      return;
    }
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await mailSettingsService.save(
        buildMailSettingsSavePayload(form, domains, { password, clientSecret }),
      );
      applySaved(saved);
      await mailSettingsService.sendTest({ to: testTo.trim() });
      setSuccess(t("mailSettings.testSuccess"));
    } catch (testError) {
      setError(
        getMailSettingsApiErrorMessage(testError, t("mailSettings.testFailed")),
      );
    } finally {
      setTesting(false);
    }
  };

  return (
    <PageListContainer>
      <Stack spacing={2} sx={{ width: 1, pb: 2 }}>
        <BoxHeader subtitle={t("mailSettings.subtitle")} />

        {error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        ) : null}

        <Grid container spacing={2} alignItems="stretch">
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {t("mailSettings.connectionSection")}
              </Typography>

              {loading ? (
                <Stack alignItems="center" py={5}>
                  <CircularProgress size={32} />
                </Stack>
              ) : (
                <Stack spacing={2} sx={{ mt: 1.5 }}>
                  <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={form.enabled}
                            onChange={(event) =>
                              setForm((prev) => ({
                                ...prev,
                                enabled: event.target.checked,
                              }))
                            }
                          />
                        }
                        label={t("mailSettings.enabled")}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        options={providerOptions}
                        value={selectedProvider}
                        disableClearable
                        autoHighlight
                        openOnFocus
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        onChange={(_, option) => {
                          if (!option) return;
                          setForm((prev) => ({
                            ...prev,
                            provider: option.value,
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("mailSettings.provider")}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label={t("mailSettings.fromAddress")}
                        value={form.fromAddress || ""}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            fromAddress: event.target.value,
                          }))
                        }
                        helperText={
                          isGraph
                            ? t("mailSettings.fromAddressGraphHint")
                            : undefined
                        }
                      />
                    </Grid>

                    {isGraph ? (
                      <>
                        <Grid size={12}>
                          <Alert severity="info">
                            {t("mailSettings.o365Help")}
                          </Alert>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label={t("mailSettings.tenantId")}
                            value={form.tenantId || ""}
                            onChange={(event) =>
                              setForm((prev) => ({
                                ...prev,
                                tenantId: event.target.value,
                              }))
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label={t("mailSettings.clientId")}
                            value={form.clientId || ""}
                            onChange={(event) =>
                              setForm((prev) => ({
                                ...prev,
                                clientId: event.target.value,
                              }))
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type="password"
                            autoComplete="new-password"
                            label={
                              clientSecretSet
                                ? t("mailSettings.clientSecretKeep")
                                : t("mailSettings.clientSecret")
                            }
                            value={clientSecret}
                            onChange={(event) =>
                              setClientSecret(event.target.value)
                            }
                            helperText={
                              clientSecretSet
                                ? t("mailSettings.clientSecretHint")
                                : undefined
                            }
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label={t("mailSettings.host")}
                            value={form.host || ""}
                            onChange={(event) =>
                              setForm((prev) => ({
                                ...prev,
                                host: event.target.value,
                              }))
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Autocomplete
                            options={portOptions}
                            value={selectedPort}
                            disableClearable
                            autoHighlight
                            openOnFocus
                            freeSolo
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.label
                            }
                            isOptionEqualToValue={(a, b) => {
                              const left =
                                typeof a === "string" ? Number(a) : a.value;
                              const right =
                                typeof b === "string" ? Number(b) : b.value;
                              return left === right;
                            }}
                            onChange={(_, option) => {
                              if (option == null) return;
                              const next =
                                typeof option === "string"
                                  ? Number(option)
                                  : option.value;
                              setForm((prev) => ({
                                ...prev,
                                port:
                                  Number.isFinite(next) && next > 0
                                    ? next
                                    : 587,
                              }));
                            }}
                            onInputChange={(_, value, reason) => {
                              if (reason !== "input") return;
                              const next = Number(value);
                              if (Number.isFinite(next) && next > 0) {
                                setForm((prev) => ({ ...prev, port: next }));
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("mailSettings.port")}
                                size="small"
                              />
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            size="small"
                            fullWidth
                            autoComplete="off"
                            label={t("mailSettings.username")}
                            value={form.username || ""}
                            onChange={(event) =>
                              setForm((prev) => ({
                                ...prev,
                                username: event.target.value,
                              }))
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            size="small"
                            fullWidth
                            type="password"
                            autoComplete="new-password"
                            label={
                              passwordSet
                                ? t("mailSettings.passwordKeep")
                                : t("mailSettings.password")
                            }
                            value={password}
                            onChange={(event) =>
                              setPassword(event.target.value)
                            }
                            helperText={
                              passwordSet
                                ? t("mailSettings.passwordHint")
                                : undefined
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={form.startTls}
                                onChange={(event) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    startTls: event.target.checked,
                                  }))
                                }
                              />
                            }
                            label={t("mailSettings.startTls")}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid size={12}>
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          alignItems={{ xs: "stretch", sm: "flex-start" }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            label={t("mailSettings.allowedDomains")}
                            value={domainInput}
                            onChange={(event) =>
                              setDomainInput(event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                addDomain();
                              }
                            }}
                            helperText={t("mailSettings.allowedDomainsHint")}
                          />
                          <Button
                            {...pageButtonOutlinedPrimaryProps}
                            onClick={addDomain}
                            disabled={!domainInput.trim()}
                            sx={{ flexShrink: 0, textTransform: "none" }}
                          >
                            {t("mailSettings.addDomain")}
                          </Button>
                        </Stack>
                        {domains.length > 0 ? (
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            useFlexGap
                            gap={1}
                          >
                            {domains.map((domain) => (
                              <Chip
                                key={domain}
                                label={domain}
                                size="small"
                                onDelete={() => void removeDomain(domain)}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            {t("mailSettings.allowedDomainsEmpty")}
                          </Alert>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      {...pageButtonPrimaryProps}
                      onClick={() => void handleSave()}
                      disabled={saving || testing}
                      sx={{ textTransform: "none" }}
                    >
                      {saving ? t("common.saving") : t("common.save")}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {t("mailSettings.testSection")}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.5, flex: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t("mailSettings.testTo")}
                  value={testTo}
                  onChange={(event) => setTestTo(event.target.value)}
                />
                <Button
                  {...pageButtonOutlinedPrimaryProps}
                  onClick={() => void handleTest()}
                  disabled={testing || saving || loading}
                  sx={{ textTransform: "none", alignSelf: "flex-start" }}
                >
                  {testing
                    ? t("mailSettings.testing")
                    : t("mailSettings.sendTest")}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </PageListContainer>
  );
}

function BoxHeader({ subtitle }: { subtitle: string }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {subtitle}
    </Typography>
  );
}

export default MailSettingsPage;
